import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { AlertCircle, Loader2 } from 'lucide-react'
import { LuInstagram as Instagram } from 'react-icons/lu'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { handleInstagramCallback } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'

const InstagramCallback = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { refreshUser } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [username, setUsername] = useState<string | null>(null)
  const [missingInsights, setMissingInsights] = useState(false)
  const [isProcessing, setIsProcessing] = useState(true)
  // The code can only be used once, and React strict mode runs effects twice in development
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    const processCallback = async () => {
      const urlParams = new URLSearchParams(location.search)
      const code = urlParams.get('code')
      const state = urlParams.get('state')
      const errorParam = urlParams.get('error')
      const errorDescription = urlParams.get('error_description')

      if (errorParam) {
        setError(errorDescription || errorParam)
        setIsProcessing(false)
        return
      }

      if (!code || !state) {
        setError('No authorization code received from Instagram')
        setIsProcessing(false)
        return
      }

      try {
        const result = await handleInstagramCallback(code, state)
        // Settings reads the connected accounts from the signed-in athlete, loaded before this
        await refreshUser()
        setUsername(result.username)
        setMissingInsights(result.missing_permissions.length > 0)
        setIsProcessing(false)
        // Leave the page up longer when permissions were declined, so the note can be read
        setTimeout(
          () => navigate('/settings?tab=social'),
          result.missing_permissions.length > 0 ? 5000 : 1500,
        )
      } catch (err) {
        console.error('Error connecting Instagram:', err)
        setError(err instanceof Error ? err.message : 'Failed to connect your Instagram account')
        setIsProcessing(false)
      }
    }

    processCallback()
  }, [location.search, navigate, refreshUser])

  const handleRetry = () => {
    navigate('/connect/instagram')
  }

  const handleCancel = () => {
    navigate('/settings')
  }

  return (
    <div className="w-full max-w-2xl mx-auto pb-4 min-h-screen flex items-center justify-center">
      <Card variant="dark" className="overflow-hidden w-full">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-linear-to-r from-[#FF3A89] to-[#FFC702] p-4 rounded-full">
              <Instagram className="h-8 w-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-white text-xl">
            {isProcessing ? 'Connecting Instagram' : error ? 'Connection Failed' : 'Connected'}
          </CardTitle>
          <CardDescription className="text-gray-400">
            {isProcessing
              ? 'Finishing your Instagram connection...'
              : error
                ? 'There was an issue connecting your account'
                : `Connected @${username}`}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          {isProcessing ? (
            <div className="flex flex-col items-center space-y-4">
              <Loader2 className="h-8 w-8 text-[#C0FD72] animate-spin" />
              <p className="text-gray-300">Please wait a moment...</p>
            </div>
          ) : error ? (
            <div className="space-y-4">
              <Alert className="bg-red-500/10 border-red-500/20">
                <AlertCircle className="h-4 w-4 text-red-400" />
                <AlertDescription className="text-red-300">{error}</AlertDescription>
              </Alert>

              <div className="space-y-3">
                <p className="text-gray-300 text-sm">
                  If you're experiencing issues, please check that:
                </p>
                <ul className="text-left text-sm text-gray-400 space-y-1 max-w-md mx-auto">
                  <li>• Your Instagram account is a "Business" or "Creator" account</li>
                  <li>• You approved the permissions Instagram asked for</li>
                </ul>
              </div>

              <div className="flex gap-3 justify-center pt-4">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  className="border-white/20 text-gray-300 hover:bg-white/5"
                >
                  Cancel
                </Button>
                <Button onClick={handleRetry} variant="lime">
                  Try Again
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                <div className="w-6 h-6 bg-green-500 rounded-full" />
              </div>
              <p className="text-green-300">Your Instagram account is connected.</p>
              {missingInsights && (
                <Alert className="bg-amber-500/10 border-amber-500/20">
                  <AlertDescription className="text-amber-300">
                    You didn't allow access to insights, so your performance stats won't be
                    available. You can reconnect and approve all permissions at any time.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default InstagramCallback
